import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { authService } from '../services/auth.service';

// Tipo parcial de perfil con solo los campos necesarios
type Profile = {
  id: string;
  display_name: string | null;
  avatar_url: string | null;
  banner_url: string | null;
  bio: string | null;
  role: string | null;
  created_at: string | null;
  updated_at?: string | null;
};

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  session: Session | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const currentUserIdRef = useRef<string | null>(null);
  const profileRequestRef = useRef(0);

  const loadUserProfile = useCallback(async (userId: string) => {
    const requestId = ++profileRequestRef.current;
    const isCurrentRequest = () =>
      currentUserIdRef.current === userId && profileRequestRef.current === requestId;
    try {
      const profileData = await authService.getUserProfile(userId);
      if (isCurrentRequest()) setProfile(profileData);
    } catch (error) {
      if (isCurrentRequest()) setProfile(null);
    } finally {
      if (isCurrentRequest()) setLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    let profileTimer: number | undefined;
    const profileRequest = profileRequestRef;
    const { data: { subscription } } = authService.onAuthStateChange((event, nextSession) => {
      if (!mounted) return;

      const nextUserId = nextSession?.user?.id ?? null;
      const userChanged = currentUserIdRef.current !== nextUserId;
      currentUserIdRef.current = nextUserId;
      setSession(nextSession);
      setUser(nextSession?.user ?? null);

      if (!nextUserId) {
        window.clearTimeout(profileTimer);
        ++profileRequest.current;
        setProfile(null);
        setLoading(false);
        return;
      }

      if (userChanged || event === 'USER_UPDATED' || event === 'INITIAL_SESSION') {
        window.clearTimeout(profileTimer);
        if (userChanged) setProfile(null);
        setLoading(true);
        // Supabase calls must run after the auth callback returns.
        profileTimer = window.setTimeout(() => {
          if (mounted && currentUserIdRef.current === nextUserId) {
            void loadUserProfile(nextUserId);
          }
        }, 0);
      }
    });

    return () => {
      mounted = false;
      window.clearTimeout(profileTimer);
      ++profileRequest.current;
      subscription.unsubscribe();
    };
  }, [loadUserProfile]);

  const signInWithGoogle = async () => {
    try {
      await authService.signInWithGoogle();
    } catch (error) {
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await authService.signOut();
      setUser(null);
      setProfile(null);
      setSession(null);
    } catch (error) {
      throw error;
    }
  };

  const refreshProfile = async () => {
    if (user) {
      await loadUserProfile(user.id);
    }
  };

  const value = {
    user,
    profile,
    session,
    loading,
    signInWithGoogle,
    signOut,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
